import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import DossierCard from "../components/DossierCard";
import MediaCard from "../components/MediaCard";
import api from "../lib/api";
import { useZoom } from "../context/ZoomContext";

export default function Home() {
  const [stats, setStats] = useState({ total_items: 0, dossiers: 0, media_types: 0 });
  const [dossiers, setDossiers] = useState([]);
  const [recent, setRecent] = useState([]);
  const { gridClass } = useZoom();

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dossiers").then((r) => setDossiers(r.data || [])).catch(() => {});
    api.get("/media/recent", { params: { limit: 24 } }).then((r) => setRecent(r.data || [])).catch(() => {});
  }, []);

  return (
    <Layout>
      {/* Hero */}
      <section data-testid="hero-section" className="px-4 md:px-6 pt-6 md:pt-12 pb-8 md:pb-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-4xl w-full min-w-0">
            <div className="mono text-[10px] sm:text-[11px] uppercase tracking-[0.14em] sm:tracking-[0.16em] text-ink-muted mb-4 md:mb-5 flex items-center gap-2 flex-wrap">
              <span className="rec-dot" />
              // Classified · Open Access · Est. 2026
            </div>
            <h1 className="heavy text-[11vw] sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl leading-[0.9] break-words hyphens-none">
              THE CURATED<br />
              <span className="underline-yellow">INTELLIGENCE</span><br />VAULT.
            </h1>
            <p className="mono text-[12px] sm:text-[13px] text-ink-muted mt-5 md:mt-6 max-w-[54ch]">
              Rally footage, declassified conflict, UAP encounters, esoteric texts, vintage cartography, ephemera — aggregated, indexed, curated.
            </p>
          </div>

          <div className="flex flex-wrap gap-3 items-end">
            <Stat n={stats.total_items} label="Items" />
            <Stat n={stats.dossiers} label="Dossiers" />
            <Stat n={stats.media_types} label="Types" />
          </div>
        </div>

        <div className="mt-6 md:mt-8 flex flex-wrap gap-2">
          <Link to="/dossiers" data-testid="hero-browse-btn" className="pill pill-yellow">Browse dossiers →</Link>
          <Link to="/search" data-testid="hero-search-btn" className="pill">Search</Link>
          <Link to="/new" className="pill">New arrivals</Link>
        </div>
      </section>

      {/* Dossiers grid */}
      {dossiers.length > 0 && (
        <section className="px-2 md:px-3 pb-4">
          <SectionHeader title="Dossiers" link={{ to: "/dossiers", label: "All →" }} />
          <div data-testid="dossiers-grid" className={`grid ${gridClass} gap-2 md:gap-3`}>
            {dossiers.map((d) => <DossierCard key={d.id} dossier={d} />)}
          </div>
        </section>
      )}

      {/* Recent grid */}
      {recent.length > 0 && (
        <section className="px-2 md:px-3 py-8">
          <SectionHeader title="New Arrivals" link={{ to: "/new", label: "View all →" }} />
          <div data-testid="recent-grid" className={`grid ${gridClass} gap-2 md:gap-3`}>
            {recent.slice(0, 12).map((m) => <MediaCard key={m.id} media={m} />)}
          </div>
        </section>
      )}

      {dossiers.length === 0 && recent.length === 0 && (
        <div className="px-4 md:px-6 py-12">
          <div className="hairline p-12 text-center">
            <div className="heavy text-3xl mb-2">Awaiting first transmission.</div>
            <div className="mono text-[12px] text-ink-muted">Log into /admin to ingest your archive.</div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const Stat = ({ n, label }) => (
  <div className="text-right">
    <div className="heavy text-5xl leading-none">{String(n ?? 0)}</div>
    <div className="mono text-[10px] uppercase tracking-[0.14em] text-ink-muted mt-1">{label}</div>
  </div>
);

const SectionHeader = ({ title, link }) => (
  <div className="flex items-end justify-between mb-4 px-2 md:px-1">
    <h2 className="heavy text-4xl md:text-5xl">{title}</h2>
    {link && <Link to={link.to} className="pill pill-sm">{link.label}</Link>}
  </div>
);
